//WebSocketListener.tsx:

import { useCallback, useEffect, useRef } from 'react';
import {
    addAction,
    applyActionTransferProgress,
    clearActionTrackingState,
    hydrateActionSnapshots,
    hydrateActiveActionSnapshots,
    receiveActionNotification,
    selectActions,
} from '../redux/slices/actionsSlice';

import { Client } from '@stomp/stompjs';
import { AppDispatch } from '../redux/store/store';
import { Action, ActionRunSummary } from '../types/actionTypes';
import type { ActionTransferProgress } from '../types/actionTypes';
import { actionRunDetailToAction, isActionRunDetailPayload } from '../utils/actionRunMapper';
import { isKeycloakEnabled } from '../config/authConfig';
import { getWebSocketUrl } from '../config/apiConfig';
import SockJS from 'sockjs-client';
import actionRunsService from './actionRunsService';
import { useKeycloak } from '../components/ConditionalAuthProvider';
import authService from './authService';
import { terminateKeycloakSession } from '../keycloak';
import { receiveNotification } from '../redux/slices/notificationSlice';
import { fetchRTUData, fetchRTUs, updateRtuData } from '../redux/slices/rtuSlice';
import { isTerminalActionStatus, resolveActionDisplayStatus } from '../utils/actionStatusConfig';
import { getOpenRtuIdFromPath, shouldRefreshOpenRtuDetail } from '../utils/rtuDetailRefresh';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActionLaunchRequest } from './rtuService';
import {
    clearPendingActionLaunches,
    listPendingActionLaunches,
    removePendingActionLaunch,
} from './pendingActionLaunchStorage';

const WS_RECONNECT_DELAY_MS = 5000;
const WS_RECONNECT_MAX_DELAY_MS = 30000;
const ACTIVE_HYDRATION_COALESCE_MS = 5000;

const WebSocketListener = () => {
    const stompClientRef = useRef<Client|null>(null);
    const websocketFailureCountRef = useRef(0);
    const detailRefreshTimerRef = useRef<number | null>(null);
    const detailRefreshPendingRtuIdRef = useRef<string | null>(null);
    const detailRefreshInFlightRtuIdRef = useRef<string | null>(null);
    const activeHydrationInFlightRef = useRef<Promise<void> | null>(null);
    const activeHydrationTimerRef = useRef<number | null>(null);
    const lastActiveHydrationStartedAtRef = useRef(0);
    const trackedActionsRef = useRef<Action[]>([]);
    const { keycloak } = useKeycloak();
    const dispatch = useDispatch<AppDispatch>();
    const trackedActions = useSelector(selectActions);
    const keycloakMode = isKeycloakEnabled();

    useEffect(() => {
        trackedActionsRef.current = trackedActions;
    }, [trackedActions]);

    const disconnectStompClient = useCallback(() => {
        if (stompClientRef.current) {
            stompClientRef.current.deactivate();
            stompClientRef.current = null;
        }
    }, []);

    const flushDetailRefresh = useCallback(() => {
        const nextRtuId = detailRefreshPendingRtuIdRef.current;
        if (!nextRtuId || detailRefreshInFlightRtuIdRef.current) {
            return;
        }

        detailRefreshPendingRtuIdRef.current = null;
        detailRefreshInFlightRtuIdRef.current = nextRtuId;

        void dispatch(fetchRTUData(nextRtuId)).finally(() => {
            detailRefreshInFlightRtuIdRef.current = null;
            if (detailRefreshPendingRtuIdRef.current) {
                flushDetailRefresh();
            }
        });
    }, [dispatch]);

    const scheduleDetailRefresh = useCallback((updatedRtu: unknown) => {
        const openRtuId = shouldRefreshOpenRtuDetail(window.location.pathname, updatedRtu);
        if (!openRtuId) {
            return;
        }

        detailRefreshPendingRtuIdRef.current = openRtuId;
        if (detailRefreshTimerRef.current) {
            window.clearTimeout(detailRefreshTimerRef.current);
        }

        detailRefreshTimerRef.current = window.setTimeout(() => {
            detailRefreshTimerRef.current = null;
            flushDetailRefresh();
        }, 750);
    }, [flushDetailRefresh]);

    const requestRtuStatusReconciliation = useCallback(() => {
        // Bug 3154: bounded reconciliation after a (re)connect - never a
        // continuous full-fleet poll. A missed canonical-status event during
        // a connection gap must not leave Assets/Grouped Assets/Map/Dashboard
        // stuck on a stale snapshot after the socket comes back.
        void dispatch(fetchRTUs({}));
        const openRtuId = getOpenRtuIdFromPath(window.location.pathname);
        if (openRtuId) {
            void dispatch(fetchRTUData(openRtuId));
        }
    }, [dispatch]);

    const reconcilePendingLaunches = useCallback(async () => {
        const pendingLaunches = listPendingActionLaunches();
        await Promise.allSettled(pendingLaunches.map(async (pending) => {
            try {
                const status = await fetchActionLaunchRequest(pending.idempotencyKey);
                if (status.action) {
                    dispatch(addAction(status.action));
                    removePendingActionLaunch(pending.idempotencyKey);
                    return;
                }
                if (String(status.state).toUpperCase() === 'REJECTED') {
                    removePendingActionLaunch(pending.idempotencyKey);
                }
            } catch (error: unknown) {
                const responseStatus = (error as { response?: { status?: number } })?.response?.status;
                // A missing or temporarily unavailable recovery record must remain
                // available for a same-key reconciliation/replay from the action UI.
                if (responseStatus !== 404) {
                    console.warn('Pending action launch reconciliation is temporarily unavailable.');
                }
            }
        }));
    }, [dispatch]);

    const hydrateActiveActions = useCallback(async () => {
        try {
            const summaries: ActionRunSummary[] = [];
            let pageNumber = 0;
            let totalPages = 1;

            // Active hydration is authoritative. Page through the complete visible
            // active set so reconnect cannot silently retain inaccessible rows.
            while (pageNumber < totalPages && pageNumber < 100) {
                const page = await actionRunsService.fetchActiveActionRuns({ page: pageNumber, size: 100 });
                summaries.push(...page.content);
                totalPages = Math.max(page.totalPages, 1);
                pageNumber += 1;
            }

            const activeIds = new Set(
                summaries
                    .map((run) => run.id)
                    .filter((id) => Number.isFinite(id))
            );
            const trackedNonTerminalIds = trackedActionsRef.current
                .filter((action) => !isTerminalActionStatus(resolveActionDisplayStatus(action)))
                .map((action) => action.id)
                .filter((id) => Number.isFinite(id));
            const requestedIds = Array.from(new Set([
                ...Array.from(activeIds),
                ...trackedNonTerminalIds,
            ])).slice(0, 200);
            const detailResults = await Promise.allSettled(
                requestedIds.map((actionId) => actionRunsService.fetchActionRun(actionId))
            );
            const visibleDetails = detailResults
                .filter((result): result is PromiseFulfilledResult<Awaited<ReturnType<typeof actionRunsService.fetchActionRun>>> => result.status === 'fulfilled')
                .map((result) => result.value);
            const snapshots = visibleDetails.map(actionRunDetailToAction);

            // The active endpoint is authoritative for tracker cleanup. Reconcile
            // previously tracked nonterminal IDs as well, because a terminal update
            // missed during a connection gap no longer appears in /active.
            dispatch(hydrateActiveActionSnapshots(
                snapshots.filter((action) => activeIds.has(action.id))
            ));
            dispatch(hydrateActionSnapshots(snapshots));
        } catch (error) {
            console.warn('Failed to hydrate active actions after WebSocket connection:', error);
        }
    }, [dispatch]);

    const requestActiveActionHydration = useCallback(() => {
        if (activeHydrationInFlightRef.current) {
            return;
        }

        const elapsed = Date.now() - lastActiveHydrationStartedAtRef.current;
        const delay = Math.max(0, ACTIVE_HYDRATION_COALESCE_MS - elapsed);
        if (activeHydrationTimerRef.current) {
            window.clearTimeout(activeHydrationTimerRef.current);
            activeHydrationTimerRef.current = null;
        }

        const startHydration = () => {
            activeHydrationTimerRef.current = null;
            lastActiveHydrationStartedAtRef.current = Date.now();
            const hydration = hydrateActiveActions();
            activeHydrationInFlightRef.current = hydration;
            void hydration.finally(() => {
                if (activeHydrationInFlightRef.current === hydration) {
                    activeHydrationInFlightRef.current = null;
                }
            });
        };

        if (delay === 0) {
            startHydration();
            return;
        }
        activeHydrationTimerRef.current = window.setTimeout(startHydration, delay);
    }, [hydrateActiveActions]);

    useEffect(() => {
        // Get token from either Keycloak or backend auth
        let token: string | null = null;

        if (keycloakMode && keycloak?.authenticated && keycloak?.token) {
            token = keycloak.token;
        } else if (!keycloakMode) {
            token = authService.getValidToken(true);
        }

        if (!token) {
            console.warn('No authentication token available. WebSocket not established.');
            disconnectStompClient();
            return;
        }

        let backendTokenMonitorHandle: number | null = null;
        const handleForcedLogout = () => {
            disconnectStompClient();
            dispatch(clearActionTrackingState());
            clearPendingActionLaunches();
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('dms-auth-logout', handleForcedLogout as EventListener);
        }

        // Use the WebSocket URL that connects directly to the backend.
        // The STOMP client must create a fresh SockJS instance per connection attempt;
        // reusing a previously closed socket leads to noisy reconnect churn.
        const socketUrl = getWebSocketUrl() + '/ws';

        const stompClient = new Client({
            webSocketFactory: () => new SockJS(socketUrl),
            connectHeaders: {
              Authorization: `Bearer ${token}`,
            },
            connectionTimeout: 10000,
            reconnectDelay: WS_RECONNECT_DELAY_MS,
            onConnect: () => {
                websocketFailureCountRef.current = 0;
                stompClient.reconnectDelay = WS_RECONNECT_DELAY_MS;

                // Subscribe to RTU updates
                stompClient.subscribe('/topic/partialRtuUpdate', (message) => {
                    try {
                        const updatedRtu = JSON.parse(message.body);
                        dispatch(updateRtuData(updatedRtu));
                        scheduleDetailRefresh(updatedRtu);
                    } catch (error) {
                        console.error('Error parsing RTU update:', error);
                    }
                });

                // Subscribe to action notifications
                stompClient.subscribe('/user/queue/action-notifications', (message) => {
                    try {
                        const notification = JSON.parse(message.body);
                        dispatch(receiveActionNotification(notification));
                        dispatch(receiveNotification(notification));
                    } catch (error) {
                        console.error('Error parsing notification:', error);
                    }
                });

                // Subscribe to actions
                stompClient.subscribe('/user/queue/actions', (message) => {
                    try {
                        const payload = JSON.parse(message.body);
                        const action: Action = isActionRunDetailPayload(payload) ? actionRunDetailToAction(payload) : payload;
                        dispatch(addAction(action));
                    } catch (error) {
                        console.error('Error parsing action:', error);
                    }
                });

                stompClient.subscribe('/user/queue/action-transfer-progress', (message) => {
                    try {
                        const progress = JSON.parse(message.body) as ActionTransferProgress;
                        if (progress.schemaVersion === 1 && Number.isFinite(progress.actionId)
                            && Number.isFinite(progress.targetRunId) && Number.isFinite(progress.sequence)) {
                            dispatch(applyActionTransferProgress(progress));
                        }
                    } catch (error) {
                        console.error('Error parsing action transfer progress:', error);
                    }
                });

                // Subscribe before REST reconciliation so an update cannot be lost
                // between the connection acknowledgement and state hydration.
                requestActiveActionHydration();
                void reconcilePendingLaunches();
                requestRtuStatusReconciliation();
            },
            onStompError: (frame) => {
                console.error('Broker reported error:', frame.headers['message']);
                console.error('Additional details:', frame.body);
                const details = `${frame.headers['message'] || ''} ${frame.body || ''}`.toLowerCase();
                if (details.includes('401') || details.includes('403') || details.includes('unauthor')) {
                    if (keycloakMode) {
                        void terminateKeycloakSession('ws-auth-error');
                    } else {
                        authService.handleAuthFailure({ redirectToLogin: true, reason: 'ws-auth-error' });
                    }
                    disconnectStompClient();
                }
            },
            onWebSocketClose: () => {
                if (stompClient.connected) {
                    return;
                }

                websocketFailureCountRef.current += 1;
                const nextDelay = Math.min(
                    WS_RECONNECT_DELAY_MS * (2 ** Math.min(websocketFailureCountRef.current - 1, 3)),
                    WS_RECONNECT_MAX_DELAY_MS
                );
                stompClient.reconnectDelay = nextDelay;
                console.warn(`WebSocket connection closed before STOMP connect; next retry in ${nextDelay}ms`);
            },
        });

        if (!keycloakMode && typeof window !== 'undefined') {
            backendTokenMonitorHandle = window.setInterval(() => {
                const validToken = authService.getValidToken(true);
                if (!validToken) {
                    console.warn('Backend token expired while WebSocket was active; disconnecting.');
                    disconnectStompClient();
                }
            }, 15000);
        }

        // Store reference for cleanup
        stompClientRef.current = stompClient;

        // Activate the client
        stompClient.activate();

        // Cleanup: Disconnect the client on component unmount
        return () => {
            if (detailRefreshTimerRef.current) {
                window.clearTimeout(detailRefreshTimerRef.current);
                detailRefreshTimerRef.current = null;
            }
            if (activeHydrationTimerRef.current) {
                window.clearTimeout(activeHydrationTimerRef.current);
                activeHydrationTimerRef.current = null;
            }
            if (typeof window !== 'undefined') {
                window.removeEventListener('dms-auth-logout', handleForcedLogout as EventListener);
            }
            if (backendTokenMonitorHandle) {
                window.clearInterval(backendTokenMonitorHandle);
            }
            stompClient.deactivate();
            stompClientRef.current = null;
        };
    }, [dispatch, disconnectStompClient, keycloak?.authenticated, keycloak?.token, keycloakMode, reconcilePendingLaunches, requestActiveActionHydration, requestRtuStatusReconciliation, scheduleDetailRefresh]);

    return null; // This component doesn't render anything itself
};

export default WebSocketListener;
